import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@chapche/ui';
import { propertiesStorage } from '../../../services/propertiesStorage';
import { propertyNamesService } from '../../../services/propertyNamesService';
import {
    PAGE_TAB_NAME_LIST,
    PRODUCT_TAB,
    PROPERTY_TAB_NAME,
} from '../../../constant';
import {
    buildMacronutrientCompositionTree,
    type CompositionProperty,
    type CompositionTreeNode,
} from '../../../services/propertyCompositionTree';
import {
    layoutCompositionBubbles,
    type LaidOutBubble,
} from '../../../services/compositionBubbleLayout';

const FILL = '#3d3d3d';
const HALO = 'rgba(160, 160, 160, 0.38)';
const EDGE = '#b5b5b5';
const LABEL = '#2f2f2f';
const LABEL_MUTED = '#7a7a7a';
const INSIDE = '#f4f4f4';

type I18nLike = {
    resolvedLanguage?: string;
    t: (key: string) => string;
};

const primaryLabel = (node: CompositionTreeNode, i18n: I18nLike): string => {
    if (node.property) {
        return propertyNamesService.getLocalization(
            i18n.resolvedLanguage,
            node.property,
            true,
        );
    }
    if (node.labelKey) {
        return i18n.t(node.labelKey);
    }
    return node.code || node.id;
};

const secondaryLabel = (node: CompositionTreeNode): string => {
    if (node.code && node.property) {
        return node.code;
    }
    return '';
};

const badgeHint = (badges: string[], i18n: I18nLike): string => {
    if (badges.length === 0) {
        return '';
    }
    return badges
        .map((badge) => i18n.t(`app.page.property.composition.badge.${badge}`))
        .join(' · ');
};

const A11yTree = ({
    nodes,
    i18n,
}: {
    nodes: CompositionTreeNode[];
    i18n: I18nLike;
}) => (
    <ul>
        {nodes.map((node) => (
            <li key={node.id}>
                {primaryLabel(node, i18n)}
                {node.code ? ` (${node.code})` : ''}
                {node.emptyHintKey ? ` — ${i18n.t(node.emptyHintKey)}` : ''}
                {node.children.length > 0 ? (
                    <A11yTree nodes={node.children} i18n={i18n} />
                ) : null}
            </li>
        ))}
    </ul>
);

const BubbleNode = ({
    bubble,
    i18n,
    onOpen,
}: {
    bubble: LaidOutBubble;
    i18n: I18nLike;
    onOpen: (node: CompositionTreeNode) => void;
}) => {
    const { node, x, y, r, hasHalo, labelMode, labelSide, kind } = bubble;
    const title = primaryLabel(node, i18n);
    const code = secondaryLabel(node);
    const hint = badgeHint(node.badges, i18n);
    const empty = node.emptyHintKey ? i18n.t(node.emptyHintKey) : '';
    const clickable = Boolean(node.property?.id);
    const labelX = labelSide === 'right' ? x + r + 8 : x - r - 8;
    const anchor = labelSide === 'right' ? 'start' : 'end';

    return (
        <g
            role={clickable ? 'button' : 'img'}
            tabIndex={clickable ? 0 : undefined}
            aria-label={title}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
            onClick={() => {
                if (clickable) {
                    onOpen(node);
                }
            }}
            onKeyDown={(event) => {
                if (clickable && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onOpen(node);
                }
            }}
        >
            {hasHalo ? (
                <circle
                    cx={x}
                    cy={y}
                    r={r * 1.55}
                    fill={HALO}
                    pointerEvents="none"
                />
            ) : null}
            <circle
                cx={x}
                cy={y}
                r={r}
                fill={FILL}
                stroke="none"
            />
            {labelMode === 'inside' ? (
                <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={INSIDE}
                    style={{ fontSize: kind === 'root' ? 13 : 11, pointerEvents: 'none' }}
                >
                    <tspan x={x} dy="-0.35em">{title}</tspan>
                    {code ? (
                        <tspan
                            x={x}
                            dy="1.25em"
                            fill="rgba(255,255,255,0.7)"
                            style={{ fontSize: 10 }}
                        >
                            {code}
                        </tspan>
                    ) : null}
                </text>
            ) : (
                <text
                    x={labelX}
                    y={y}
                    textAnchor={anchor}
                    dominantBaseline="middle"
                    fill={LABEL}
                    style={{ fontSize: kind === 'aggregate' ? 12 : 11, pointerEvents: 'none' }}
                >
                    <tspan x={labelX} dy={hint || empty || code ? '-0.55em' : '0'}>
                        {title}
                    </tspan>
                    {code ? (
                        <tspan
                            x={labelX}
                            dy="1.15em"
                            fill={LABEL_MUTED}
                            style={{ fontSize: 10 }}
                        >
                            {code}
                        </tspan>
                    ) : null}
                    {hint ? (
                        <tspan
                            x={labelX}
                            dy="1.15em"
                            fill={LABEL_MUTED}
                            style={{ fontSize: 9 }}
                        >
                            {hint}
                        </tspan>
                    ) : null}
                    {empty ? (
                        <tspan
                            x={labelX}
                            dy="1.15em"
                            fill={LABEL_MUTED}
                            style={{ fontSize: 9 }}
                        >
                            {empty}
                        </tspan>
                    ) : null}
                </text>
            )}
            {labelMode === 'inside' && empty ? (
                <text
                    x={x}
                    y={y + r + 14}
                    textAnchor="middle"
                    fill={LABEL_MUTED}
                    style={{ fontSize: 10, pointerEvents: 'none' }}
                >
                    {empty}
                </text>
            ) : null}
        </g>
    );
};

const PropertyCompositionTreeTab = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const [revision, setRevision] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState({ x: 0, y: 0, k: 1 });
    const dragRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
    } | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);

    useEffect(() => {
        const onChange = () => {
            setIsLoading(false);
            setRevision((value) => value + 1);
        };
        propertiesStorage.loadItems(onChange);
        propertiesStorage.registerFiltersChangingObserver(onChange);
        return () => {
            propertiesStorage.unRegisterFiltersChangingObserver(onChange);
        };
    }, []);

    const tree = useMemo(() => {
        const items = propertiesStorage.getItems(true) as CompositionProperty[];
        return buildMacronutrientCompositionTree(items || []);
    }, [revision]);

    const layout = useMemo(() => layoutCompositionBubbles(tree), [tree]);

    useEffect(() => {
        setView({ x: 0, y: 0, k: 1 });
    }, [revision, layout.width, layout.height]);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) {
            return undefined;
        }
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const ctm = svg.getScreenCTM();
            if (!ctm) {
                return;
            }
            const cursor = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
            const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
            setView((prev) => {
                const nextK = Math.min(2.4, Math.max(0.45, prev.k * factor));
                const worldX = (cursor.x - prev.x) / prev.k;
                const worldY = (cursor.y - prev.y) / prev.k;
                return {
                    k: nextK,
                    x: cursor.x - worldX * nextK,
                    y: cursor.y - worldY * nextK,
                };
            });
        };
        svg.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            svg.removeEventListener('wheel', onWheel);
        };
    }, [isLoading, revision]);

    const openProperty = useCallback((node: CompositionTreeNode) => {
        if (node.property?.id) {
            navigate(`/product/property/${node.property.id}/`);
        }
    }, [navigate]);

    const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0) {
            return;
        }
        const target = event.target as Element;
        if (target.closest('[role="button"]')) {
            return;
        }
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originX: view.x,
            originY: view.y,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
            return;
        }
        setView((prev) => ({
            ...prev,
            x: drag.originX + (event.clientX - drag.startX),
            y: drag.originY + (event.clientY - drag.startY),
        }));
    };

    const onPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
        if (dragRef.current?.pointerId === event.pointerId) {
            dragRef.current = null;
        }
    };

    return (
        <section className="workspace__content w-full">
            <div className="mb-16 flex flex-wrap items-center gap-12">
                <Button
                    variant="outline"
                    onClick={() => navigate(`/${PRODUCT_TAB}/${PROPERTY_TAB_NAME}/${PAGE_TAB_NAME_LIST}`)}
                >
                    {i18n.t('app.page.property.composition.backToList')}
                </Button>
                <span className="block__text">
                    {i18n.t('app.page.property.composition.title')}
                </span>
            </div>
            <p className="block__bullet mb-16">
                {i18n.t('app.page.property.composition.hint')}
            </p>
            {isLoading ? (
                <p className="block__text">{i18n.t('app.page.property.composition.loading')}</p>
            ) : (
                <>
                    <div className="sr-only">
                        <A11yTree nodes={tree} i18n={i18n} />
                    </div>
                    <div
                        key={`composition-bubbles-${revision}`}
                        className="relative w-full overflow-hidden rounded-input bg-[#f7f7f5]"
                        style={{ minHeight: '70vh', touchAction: 'none' }}
                    >
                        <svg
                            ref={svgRef}
                            width="100%"
                            height="100%"
                            viewBox={`0 0 ${Math.max(layout.width, 640)} ${Math.max(layout.height, 480)}`}
                            className="block h-full min-h-[70vh] w-full"
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerUp}
                            role="img"
                            aria-label={i18n.t('app.page.property.composition.title')}
                        >
                            <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
                                <g aria-hidden>
                                    {layout.edges.map((edge) => (
                                        <line
                                            key={edge.id}
                                            x1={edge.x1}
                                            y1={edge.y1}
                                            x2={edge.x2}
                                            y2={edge.y2}
                                            stroke={EDGE}
                                            strokeWidth={1.25}
                                            strokeDasharray="4 5"
                                            strokeLinecap="round"
                                        />
                                    ))}
                                </g>
                                <g>
                                    {layout.bubbles.map((bubble) => (
                                        <BubbleNode
                                            key={bubble.id}
                                            bubble={bubble}
                                            i18n={i18n}
                                            onOpen={openProperty}
                                        />
                                    ))}
                                </g>
                            </g>
                        </svg>
                    </div>
                </>
            )}
        </section>
    );
};

export default PropertyCompositionTreeTab;
